/**
 * Notification System Types
 * Following the project's universal error contract and type patterns
 */

export type NotificationType = 
  | 'connection_request' 
  | 'connection_accepted'
  | 'post_like'
  | 'photo_like'
  | 'comment'
  | 'reply'
  | 'mention'
  | 'mention_edit'
  | 'comment_like';

export type NotificationEntityType = 'connection_request' | 'post' | 'photo' | 'comment' | 'reply' | null;

export interface NotificationActor {
  id: string;
  first_name: string;
  last_name: string;
  username: string;
  avatar_url: string | null;
}

export interface Notification {
  id: string;
  notification_type: NotificationType;
  entity_id: string | null;
  entity_type: NotificationEntityType;
  reference_post_id: string | null;
  reference_photo_id: string | null;
  reference_username: string | null;
  display_text: string | null;
  reference_parent_comment_id: string | null;
  read_at: string | null;
  created_at: string;
  actor: NotificationActor;
  grouped_count: number;
  latest_actors: NotificationActor[];
}

/**
 * Message templates for notification display
 * Format: "{Actor Full Name} {message}"
 */
export const NOTIFICATION_MESSAGES: Record<NotificationType, string> = {
  connection_request: 'sent you a connection request',
  connection_accepted: 'accepted your connection request',
  post_like: 'liked your post',
  photo_like: 'liked your photo',
  comment: 'commented on your post',
  reply: 'replied to your comment',
  mention: 'mentioned you',
  mention_edit: 'edited content where you were mentioned',
  comment_like: 'liked your comment',
} as const;

/**
 * Get dynamic message for mention notifications based on entity type
 */
const getMentionMessage = (entityType: NotificationEntityType): string => {
  switch (entityType) {
    case 'post':
      return 'mentioned you in a post';
    case 'comment':
      return 'mentioned you in a comment';
    case 'reply':
      return 'mentioned you in a reply';
    default:
      return 'mentioned you';
  }
};

/**
 * Get dynamic message for mention_edit notifications based on entity type
 */
const getMentionEditMessage = (entityType: NotificationEntityType): string => {
  switch (entityType) {
    case 'post':
      return 'edited a post where you were mentioned';
    case 'comment':
      return 'edited a comment where you were mentioned';
    case 'reply':
      return 'edited a reply where you were mentioned';
    default:
      return 'edited content where you were mentioned';
  }
};

/**
 * Get notification message - handles both static and dynamic messages
 * Use this function instead of accessing NOTIFICATION_MESSAGES directly
 */
export const getNotificationMessage = (
  type: NotificationType,
  entityType?: NotificationEntityType
): string => {
  if (type === 'mention') {
    return getMentionMessage(entityType ?? null);
  }
  if (type === 'mention_edit') {
    return getMentionEditMessage(entityType ?? null);
  }
  // Dynamic message for comments based on entity_type (photo vs post)
  if (type === 'comment') {
    return entityType === 'photo' ? 'commented on your photo' : 'commented on your post';
  }
  return NOTIFICATION_MESSAGES[type];
};

/**
 * Strip HTML tags and mention markup from notification display text.
 * Converts `@[username|Display Name]` → `Display Name` and removes all HTML tags.
 */
const stripNotificationMarkup = (text: string): string =>
  text
    .replace(/<[^>]*>/g, '')                      // Strip HTML tags
    .replace(/@\[[^\]|]+\|([^\]]+)\]/g, '$1')     // @[id|Name] → Name
    .replace(/\s+/g, ' ')                         // Collapse whitespace
    .trim();

/**
 * Get grouped display text for notifications with multiple actors.
 * Single source of truth for grouped notification messages (Phase 13).
 */
export const getGroupedDisplayText = (notification: Notification): string => {
  const actorName = [notification.actor.first_name, notification.actor.last_name].filter(Boolean).join(' ');

  if (notification.grouped_count <= 1) {
    if (notification.display_text) {
      const stripped = stripNotificationMarkup(notification.display_text);
      // Connection-type display_text already includes actor name
      const typesWithActorInText: NotificationType[] = ['connection_request', 'connection_accepted'];
      if (typesWithActorInText.includes(notification.notification_type)) {
        return stripped;
      }
      return `${actorName} ${stripped}`;
    }
    return `${actorName} ${getNotificationMessage(notification.notification_type, notification.entity_type)}`;
  }
  
  const othersCount = notification.grouped_count - 1;
  const othersText = othersCount === 1 ? '1 other' : `${othersCount} others`;
  
  switch (notification.notification_type) {
    case 'post_like':
      return `${actorName} and ${othersText} liked your post`;
    case 'photo_like':
      return `${actorName} and ${othersText} liked your photo`;
    case 'comment':
      return `${actorName} and ${othersText} commented on your ${notification.entity_type === 'photo' ? 'photo' : 'post'}`;
    case 'reply':
      return `${actorName} and ${othersText} replied to your comment`;
    case 'comment_like':
      return `${actorName} and ${othersText} liked your comment`;
    default: {
      if (notification.display_text) {
        return stripNotificationMarkup(notification.display_text);
      }
      return `${actorName} and ${othersText} ${getNotificationMessage(notification.notification_type, notification.entity_type)}`;
    }
  }
};

/** Route result type for notification navigation — includes reference fields for direct resolution */
export type NotificationRouteResult = 
  | string 
  | { type: 'post_like'; postId: string; referenceUsername?: string }
  | { type: 'photo_like'; photoId: string; referencePostId?: string; referenceUsername?: string }
  | { type: 'comment'; commentId: string; referencePostId?: string; referencePhotoId?: string; referenceUsername?: string; referenceParentCommentId?: string }
  | { type: 'reply'; commentId: string; referencePostId?: string; referencePhotoId?: string; referenceUsername?: string; parentCommentId?: string }
  | { type: 'mention'; entityId: string; entityType: 'post' | 'comment' | 'reply'; referencePostId?: string; referencePhotoId?: string; referenceUsername?: string; referenceParentCommentId?: string }
  | { type: 'comment_like'; commentId: string; referencePostId?: string; referencePhotoId?: string; referenceUsername?: string; referenceParentCommentId?: string };

/**
 * Get navigation route for a notification
 * Now forwards all reference fields from notification data for direct resolution
 * (no context RPCs needed)
 */
export const getNotificationRoute = (
  notification: Notification
): NotificationRouteResult => {
  const { notification_type: type, actor, entity_id: entityId, entity_type: entityType,
    reference_post_id, reference_photo_id, reference_username, reference_parent_comment_id } = notification;

  switch (type) {
    case 'connection_request':
      return '/profile?tab=circle&subtab=requests';
    case 'connection_accepted':
      return `/profile/${actor.username}`;
    case 'post_like':
      return entityId 
        ? { type: 'post_like', postId: entityId, referenceUsername: reference_username ?? undefined } 
        : '/';
    case 'photo_like':
      return entityId 
        ? { type: 'photo_like', photoId: entityId, referencePostId: reference_post_id ?? undefined, referenceUsername: reference_username ?? undefined } 
        : '/';
    case 'comment':
      return entityId 
        ? { type: 'comment', commentId: entityId, referencePostId: reference_post_id ?? undefined, referencePhotoId: reference_photo_id ?? undefined, referenceUsername: reference_username ?? undefined, referenceParentCommentId: reference_parent_comment_id ?? undefined } 
        : '/';
    case 'reply':
      return entityId 
        ? { type: 'reply', commentId: entityId, referencePostId: reference_post_id ?? undefined, referencePhotoId: reference_photo_id ?? undefined, referenceUsername: reference_username ?? undefined, parentCommentId: reference_parent_comment_id ?? undefined } 
        : '/';
    case 'mention':
    case 'mention_edit':
      if (entityId && entityType === 'post') {
        return { type: 'post_like', postId: entityId, referenceUsername: reference_username ?? undefined };
      }
      if (entityId && (entityType === 'comment' || entityType === 'reply')) {
        return { type: 'mention', entityId, entityType, referencePostId: reference_post_id ?? undefined, referencePhotoId: reference_photo_id ?? undefined, referenceUsername: reference_username ?? undefined, referenceParentCommentId: reference_parent_comment_id ?? undefined };
      }
      return '/';
    case 'comment_like':
      return entityId 
        ? { type: 'comment_like', commentId: entityId, referencePostId: reference_post_id ?? undefined, referencePhotoId: reference_photo_id ?? undefined, referenceUsername: reference_username ?? undefined, referenceParentCommentId: reference_parent_comment_id ?? undefined } 
        : '/';
  }
};
